// sapi-admin/src/hooks/useAdminNotifications.js
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  collection,
  query,
  orderBy,
  limit as fsLimit,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase/config';

const PAGE_SIZE = 50;
const COLLECTION = 'adminNotifications';

export function useAdminNotifications({ enabled = true, onNewUnread } = {}) {
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const prevUnreadRef = useRef(0);

  // ✅ Ref pour la callback : toujours à jour, mais JAMAIS dans les deps de l'effet.
  // Ça évite de re-souscrire à Firestore chaque fois que le composant appelant
  // recrée sa fonction onNewUnread (ex: playSound non mémoïsée).
  const onNewUnreadRef = useRef(onNewUnread);
  useEffect(() => {
    onNewUnreadRef.current = onNewUnread;
  }, [onNewUnread]);

  // ── Listener temps réel unique — source de vérité pour tout l'écran ────
  useEffect(() => {
    if (!enabled) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'), fsLimit(pageSize));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.() || new Date(d.data().createdAt),
        }));

        const newUnread = items.filter((n) => !n.read).length;
        if (onNewUnreadRef.current && newUnread > prevUnreadRef.current) {
          onNewUnreadRef.current();
        }
        prevUnreadRef.current = newUnread;

        setNotifications(items);
        setHasMore(items.length === pageSize);
        setLoading(false);
      },
      (error) => {
        console.error('useAdminNotifications listener error:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
    // ⬅️ onNewUnread retiré des deps : plus aucune re-souscription liée à cette fonction
  }, [enabled, pageSize]);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const stats = useMemo(() => {
    const byType = {};
    notifications.forEach((n) => {
      byType[n.type] = (byType[n.type] || 0) + 1;
    });
    return { total: notifications.length, unread: unreadCount, byType };
  }, [notifications, unreadCount]);

  const loadMore = useCallback(() => {
    if (!hasMore) return;
    setPageSize((p) => p + PAGE_SIZE);
  }, [hasMore]);

  const markAsRead = useCallback(async (id) => {
    try {
      await updateDoc(doc(db, COLLECTION, id), { read: true });
      return true;
    } catch (error) {
      console.error('markAsRead error:', error);
      return false;
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const unread = notifications.filter((n) => !n.read);
    if (unread.length === 0) return true;
    try {
      const batch = writeBatch(db);
      unread.forEach((n) => batch.update(doc(db, COLLECTION, n.id), { read: true }));
      await batch.commit();
      return true;
    } catch (error) {
      console.error('markAllAsRead error:', error);
      return false;
    }
  }, [notifications]);

  const deleteNotification = useCallback(async (id) => {
    try {
      await deleteDoc(doc(db, COLLECTION, id));
      return true;
    } catch (error) {
      console.error('deleteNotification error:', error);
      return false;
    }
  }, []);

  const deleteMany = useCallback(async (ids) => {
    if (ids.length === 0) return { success: 0, failed: 0 };
    try {
      const batch = writeBatch(db);
      ids.forEach((id) => batch.delete(doc(db, COLLECTION, id)));
      await batch.commit();
      return { success: ids.length, failed: 0 };
    } catch (error) {
      console.error('deleteMany error:', error);
      return { success: 0, failed: ids.length };
    }
  }, []);

  const deleteAllNotifications = useCallback(
    () => deleteMany(notifications.map((n) => n.id)),
    [notifications, deleteMany]
  );

  const deleteReadNotifications = useCallback(
    () => deleteMany(notifications.filter((n) => n.read).map((n) => n.id)),
    [notifications, deleteMany]
  );

  return {
    notifications,
    unreadCount,
    stats,
    loading,
    hasMore,
    loadMore,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    deleteReadNotifications,
  };
}